import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { fetchCartAction } from "../slices/productStoreCartSlice";
import { fetchUserProfileAction } from "@/modules/hub/features/UserAppSettings/slices/userProfileSlice";
import { useCheckoutLayout } from "@/components/CheckoutLayout/CheckoutLayout";
import { useProductCheckout } from "../components/ProductCheckout/ProductCheckoutContext";

export function useProductCheckoutPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((state) => state.productStoreCart);
  const checkoutLayout = useCheckoutLayout();
  const {
    state: { currentStep },
    prevStep,
    goToStep,
    setCartItems,
    setCustomerInfo,
  } = useProductCheckout();

  const userProfile = useAppSelector((state) => state.userProfile.userProfile);

  useEffect(() => {
    dispatch(fetchCartAction());
    dispatch(fetchUserProfileAction());
  }, [dispatch]);

  useEffect(() => {
    if (items.length > 0) {
      setCartItems(items);
    }
  }, [items, setCartItems]);

  const hasPrefilledRef = useRef(false);
  useEffect(() => {
    if (userProfile && !hasPrefilledRef.current) {
      hasPrefilledRef.current = true;
      setCustomerInfo({
        firstName: userProfile.firstName || "",
        lastName: userProfile.lastName || "",
        email: userProfile.workEmail || "",
        phone: "",
      });
    }
  }, [userProfile, setCustomerInfo]);

  const backHandler = useCallback(() => {
    if (currentStep === 0) {
      navigate("/product-directory");
    } else {
      prevStep();
    }
  }, [currentStep, navigate, prevStep]);

  useEffect(() => {
    checkoutLayout?.setBackHandler(backHandler);
    return () => {
      checkoutLayout?.setBackHandler(null);
    };
  }, [checkoutLayout, backHandler]);

  const handleStepClick = useCallback(
    (step: number) => {
      if (step < currentStep) {
        goToStep(step);
      }
    },
    [currentStep, goToStep],
  );

  return {
    items,
    loading,
    currentStep,
    handleStepClick,
  };
}
